import {
  queryOptions,
  skipToken,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { z } from 'zod';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { sdrsApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type {
  QueryOptions,
  QuerySuspenseOptions,
} from '@/types/tanstack-query-options';
import type { paths } from '~/generated/sdrs/api-schema';
import {
  queryPatientResSchema,
  staffDtoSchema,
} from '~/generated/sdrs/zod-schema';

const PATH_VAR = '{id}';
const API_PATH = `/v1/patient/queryPatient/${PATH_VAR}` as const;
const QUERY_KEY = '/v1/patient/queryPatient' as const;
export const QUERY_PATIENT_BY_ID_KEY = QUERY_KEY;

type APIQueryResponse = ApiResponse<paths, typeof API_PATH>;

const staffSchema = zodRemoveNullable(staffDtoSchema);

const apiResponseDataSchema = zodRemoveNullable(queryPatientResSchema, {
  excludeMask: {
    presentation: true,
    cervicalExam: true,
    uterus: true,
    edd: true,
    specialStatus: true,
    epiduralActive: true,
    fhr: true,
    transferOutDateTime: true,
    toBed: true,
  },
}).extend({
  staff: staffSchema,
});
type APIResponseData = z.infer<typeof apiResponseDataSchema>;

const queryFn = async (id: string, signal: AbortSignal) => {
  const { data: response } = await sdrsApi.get<APIQueryResponse>(
    API_PATH.replace(PATH_VAR, id),
    { signal },
  );

  return apiResponseDataSchema.parse(response.data);
};

export const getSuspenseQueryPatientById = (id: string) => {
  return queryOptions({
    queryKey: [QUERY_KEY, id] as const,
    queryFn: ({ signal }) => queryFn(id, signal),
  });
};

export const useSuspenseQueryPatientById = (
  id: string,
  options?: QuerySuspenseOptions<
    APIResponseData,
    readonly [typeof QUERY_KEY, typeof id]
  >,
) => {
  const defaultQueryOptions = getSuspenseQueryPatientById(id);
  return useSuspenseQuery({
    ...defaultQueryOptions,
    ...options,
  });
};

const getUseQueryPatientById = (
  id?: string,
  options?: QueryOptions<APIResponseData>,
) => {
  return queryOptions({
    queryKey: [QUERY_KEY, id] as const,
    queryFn: id ? ({ signal }) => queryFn(id, signal) : skipToken,
    ...options,
  });
};

export const useQueryPatientById = (
  // NOTE: this is not patient id
  id?: string,
  options?: QueryOptions<APIResponseData>,
) => {
  return useQuery(getUseQueryPatientById(id, options));
};

export type { APIResponseData as PatientByIdData };
